/**
 * reviewApi — client for `/api/v2/payment-reviews` (BON-381 §2.3).
 *
 * Success bodies are UNWRAPPED (the object IS the body), matching the
 * bookings-v2 contract; errors are `{ status, message }` and are surfaced with
 * the server's own copy where it has any, because the interesting cases (409 on
 * a resolve two operators raced, 404 on an out-of-region read) are answers, not
 * faults.
 *
 * The `VITE_REVIEW_FIXTURES` dev backend is GONE. It existed so the pages could
 * be driven by hand before these routes existed; they exist now, and a second
 * implementation of the same five calls is a place for the two to disagree
 * without either being wrong. (`reviewFixtures.ts` stays — those are the typed
 * factories the component tests build cases from, which is an ordinary test-data
 * module and not a stand-in server.)
 */
import { apiFetch } from '../../lib/apiFetch';

import { ReviewApiError } from './reviewApiError';
import type {
  ReviewCategory,
  ReviewDetail,
  ReviewKind,
  ReviewQueueResponse,
  ReviewResolvedFilter,
} from './reviewTypes';

const BASE = '/api/v2/payment-reviews';

/** The server's message when it sent one, else copy keyed to the status. */
async function errorFrom(res: Response, what: string): Promise<ReviewApiError> {
  const body = (await res.json().catch(() => ({}))) as { message?: string };
  const fallback =
    res.status === 403
      ? "You don't have access to this item's region."
      : res.status === 404
        ? 'That item no longer exists, or is outside your regions.'
        : res.status === 409
          ? 'Someone else already changed this item — reloading it now.'
          : `${what} (${res.status})`;
  return new ReviewApiError(res.status, body.message || fallback);
}

async function get<T>(path: string, what: string, signal?: AbortSignal): Promise<T> {
  const res = await apiFetch(`${BASE}${path}`, signal ? { signal } : undefined);
  if (!res.ok) throw await errorFrom(res, what);
  return (await res.json()) as T;
}

async function post<T>(path: string, note: string, what: string): Promise<T> {
  const res = await apiFetch(`${BASE}${path}`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
  if (!res.ok) throw await errorFrom(res, what);
  return (await res.json()) as T;
}

/** GET / — both sections in one payload, each sorted oldest-first. */
export async function fetchReviewQueue(
  resolved: ReviewResolvedFilter = 'exclude',
  signal?: AbortSignal,
  category: ReviewCategory | null = null,
): Promise<ReviewQueueResponse> {
  const query = new URLSearchParams({ resolved });
  // Omitted rather than sent empty: the API 400s an unknown category, and
  // `category=` is not a category.
  if (category) query.set('category', category);
  return get<ReviewQueueResponse>(`?${query}`, 'Failed to load the review queue', signal);
}

/** GET /count — the rail badge's number. Deliberately its own endpoint so the
 *  badge never pays for the full queue payload. */
export async function fetchOpenReviewCount(signal?: AbortSignal): Promise<number> {
  const body = await get<{ openCount: number }>(
    '/count',
    'Failed to load the review count',
    signal,
  );
  return body.openCount;
}

/** GET /:kind/:id — the detail page, including the Stripe snapshot. */
export async function fetchReviewDetail(
  kind: ReviewKind,
  id: string,
  signal?: AbortSignal,
): Promise<ReviewDetail> {
  return get<ReviewDetail>(`/${kind}/${id}`, 'Failed to load this flagged item', signal);
}

/** POST /:kind/:id/resolve — the note is required; the API 400s on a blank one
 *  and the composer refuses before we get here. */
export async function resolveReview(
  kind: ReviewKind,
  id: string,
  note: string,
): Promise<ReviewDetail> {
  return post<ReviewDetail>(`/${kind}/${id}/resolve`, note, 'Failed to resolve this item');
}

/** POST /:kind/:id/reopen — also note-required, unlike the design prototype's
 *  bare Reopen button. */
export async function reopenReview(
  kind: ReviewKind,
  id: string,
  note: string,
): Promise<ReviewDetail> {
  return post<ReviewDetail>(`/${kind}/${id}/reopen`, note, 'Failed to reopen this item');
}
